import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "@/data/site";

function Counter({ value, suffix = "", decimals = 0, start }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const duration = 1600;
    const startTs = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, value]);
  const formatted = decimals ? n.toFixed(decimals) : Math.floor(n).toLocaleString("en-IN");
  return <>{formatted}{suffix}</>;
}

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      data-testid="stats-section"
      className="relative py-24 md:py-32 border-t border-slate-100 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6 mb-16">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Trust</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              Trusted by <span className="font-serif-editorial italic">businesses</span> that ship.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-lg text-slate-600 leading-relaxed">
              Growth teams, agencies and enterprises across India rely on Datorra to reach the right
              decision makers — with data that respects their time, budget and compliance posture.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100 rounded-3xl overflow-hidden border border-slate-100">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.9, ease: [0.2, 0.7, 0.15, 1] }}
              className="bg-white p-8 md:p-10 flex flex-col justify-between min-h-[220px]"
            >
              <div className="text-xs uppercase tracking-[0.22em] text-slate-400 font-medium">/{String(i + 1).padStart(2, "0")}</div>
              <div>
                <div className="font-display text-6xl md:text-7xl font-light tracking-tight text-slate-900 leading-none">
                  <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} start={inView} />
                </div>
                <div className="mt-4 text-slate-900 font-medium">{s.label}</div>
                <div className="mt-1 text-sm text-slate-500">{s.note}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
